
import React, {
  memo, useContext, useEffect, useRef, useState
} from 'react';
import GlobalContext from '../../contexts/GlobalContext';
import CoachingBar from './CoachingBar';
import CoachingBarPopup from './CoachingBarPopup';
import { CoachingBarState } from '../../types/coachingBar';
import WelcomeBackPopup from './WelcomeBackPopup';
import CommonContext from '../../contexts/common/CommonContext';
import { AppData, AppDataList, PlanAndClosetInfoResponse } from '../../types/common';
import useAppData from '../../hooks/services/ups';
import { useFetchPlanAndClosetInfo } from '../../hooks/services/common';
import { handlSlideUp } from '../../utils/coachingBarUtils';

const initialCoachingBarState: CoachingBarState = {
  closetMinimumCount: null,
  userClosetCount: null,
  userAtHomeCount: null,
  closetCountRequired: null,
  closetMinimumReachedFlag: null,
  planSize: null,
  closetSize: null,
  recommendedClosetSize: null,
  displayCoachingBar: false,
  displayCoachingPopup: false,
  displayWelcomeBackPopup: false,
  coachingBarClass: 'coaching-bar--withpopup',
  coachingPopupBtnClass: ''
};
const CoachingBarContainer = (): JSX.Element => {
  // contexts
  const { globalContext } = useContext(GlobalContext);
  const commonContextValue = useContext(CommonContext);
  const { commonContext, setCommonContext } = commonContextValue;
  const template = globalContext.gb.liquid.template;
  // useStates
  const [coachingBarState, setCoachingBarState] = useState(initialCoachingBarState);
  const [wiggleAnimationClass, setWiggleAnimationClass] = useState('');
  const [isFirstBoxShipped, setIsFirstBoxShipped] = useState(false);
  const [isError, setIsError] = useState(false);
  // refs
  const bodyRef = useRef(document.body);
  const coachingPopupRef = useRef(null);
  const coachingBarRef = useRef(null);
  // constants
  const isMobile = globalContext.gb.utils.checkDevice('mobile');
  const coachingWrapClass = isMobile ? 'show-for-small-only' : 'hide-for-small-only';
  const [coachingBarDistance, setCoachingBarDistance] = useState<string | undefined>();
  // custom hooks
  const fetchAppData = useAppData({
    onSuccess: (appData: AppDataList): void => {
      let hasFirstShipmentCompleted: AppData;

      hasFirstShipmentCompleted = appData.find((item) => item.key === 'hasFirstShipmentCompleted');

      if (hasFirstShipmentCompleted && hasFirstShipmentCompleted.value) {
        try {
          if (hasFirstShipmentCompleted.value === 'true') {
            setIsFirstBoxShipped(true);
          }
        } catch (error) {
          throw error;
        }
      }
    },
    onError: function (err: any): void {
      throw new Error(err);
    }
  });
  const fetchPlanAndClosetInfo = useFetchPlanAndClosetInfo({
    enabled: true,
    onSuccess: (planAndClosetData: PlanAndClosetInfoResponse): void => {
      const closetSummaryData = planAndClosetData.closetSummaryInfo;
      const planInfoData = planAndClosetData.planInfo;
      const recommendedSizeData = planAndClosetData.recommendedClosetSizeInfo;

      setCoachingBarState((prevState) => ({
        ...prevState,
        closetMinimumCount: closetSummaryData?.response?.closetMinThreshold,
        userClosetCount: closetSummaryData?.response?.count?.onRack,
        userAtHomeCount: closetSummaryData?.response?.count?.atHome,
        closetMinimumReachedFlag: closetSummaryData?.response?.isUserAboveClosetMin,
        closetCountRequired: Math.max(closetSummaryData?.response?.closetMinThreshold - closetSummaryData?.response?.count?.onRack, 0),
        planSize: planInfoData?.numItems,
        closetSize: closetSummaryData?.response?.closetMinThreshold,
        recommendedClosetSize: recommendedSizeData?.recommendedClosetSize,
        displayCoachingBar: globalContext.customer?.isSubscriber() &&
        closetSummaryData?.response?.count?.atHome === 0 &&
        template === 'closet' ? !commonContext.isClosetLoading : true &&
        !isFirstBoxShipped &&
        (!closetSummaryData?.response?.isUserAboveClosetMin ||
          (commonContext.isCoachingBarUpdated &&
          closetSummaryData?.response?.count?.onRack === closetSummaryData?.response?.closetMinThreshold))
      }));
    },
    onError: function (err: any): void {
      setIsError(true);
      throw new Error(err);
    },
    sourceEventId: globalContext.emitter.sourceEventId,
    clusterValue: globalContext.gb.utils.getClusterValue()
  });
  // methods
  const showWelcomeBackPopupScreen = (): void => {
    const url = document.createElement('a');

    url.href = window.document.referrer;

    if (globalContext.customer?.isSubscriber() &&
    !commonContext.isCoachingBarUpdated &&
    !coachingBarState.closetMinimumReachedFlag &&
    (!window.document.referrer || (url.hostname !== window.location.hostname))) {
      setCoachingBarState({
        ...coachingBarState,
        displayWelcomeBackPopup: true
      });
    }
  };
  const pushGAEventSysACMSuccess = (): void => {
    globalContext.gtm.pushVersionedEvent('2.x', 'Closeting', 'SysACMSuccess', 'planSize= ' + coachingBarState.planSize + ' closetMin = ' + coachingBarState.closetMinimumCount);
  };
  const updateCoachingBarState = (): void => {
    const onRackCount = parseInt(sessionStorage.getItem('onRackCount'), 10);
    // minReachedFlag condition is to avoid showing ACMpopup while decreasing the onRackCount
    const prevMinimumReachedFlag = coachingBarState.closetMinimumReachedFlag;
    const newMinimumReachedFlag = onRackCount >= coachingBarState.closetMinimumCount;

    if (commonContext.isCoachingBarUpdated && !(prevMinimumReachedFlag && newMinimumReachedFlag)) {
      setCoachingBarState((prevState) => ({
        ...prevState,
        userClosetCount: onRackCount,
        closetMinimumReachedFlag: onRackCount >= coachingBarState.closetMinimumCount,
        closetCountRequired: Math.max(coachingBarState.closetMinimumCount - onRackCount, 0),
        displayCoachingBar: globalContext.customer?.isSubscriber() &&
        coachingBarState.userAtHomeCount === 0 &&
        template === 'closet' ? !commonContext.isClosetLoading : true &&
        !isFirstBoxShipped &&
        (!(onRackCount >= coachingBarState.closetMinimumCount) ||
          (commonContext.isCoachingBarUpdated && !commonContext.isUpdatedFromCloset &&
          onRackCount === coachingBarState.closetMinimumCount)),
        displayCoachingPopup: !commonContext.isUpdatedFromCloset && onRackCount === coachingBarState.closetMinimumCount,
        coachingPopupBtnClass: onRackCount >= coachingBarState.closetMinimumCount ? 'js-coaching-popup-btn' : ''
      }));

      if (globalContext.customer?.isSubscriber() &&
      onRackCount && coachingBarState.closetMinimumCount &&
      onRackCount === coachingBarState.closetMinimumCount) {
        pushGAEventSysACMSuccess();
      }

      // wiggle animation on coachingBar
      setWiggleAnimationClass('coaching-bar--animation');
      setTimeout(() => {
        setWiggleAnimationClass('');
      }, 2000);
    }
  };
  const fetchCoachingBarData = (): void => {
    if (!fetchAppData.isFetched) {
      fetchAppData.refetch();
    }

    if (!fetchPlanAndClosetInfo.isFetched) {
      fetchPlanAndClosetInfo.refetch();
    }
  };
  const handleCoachingBarDistance = (): void => {
    // to adjusts coaching Bar according to the header height for mobile
    if (!isMobile) {
      return;
    }

    const headerElement = document.querySelector('.header-elements');
    const mainSJHeader: HTMLElement = document.querySelector('#main-sj-header');

    // for non-sj
    if (headerElement) {
      const headerElemClientHeight = `${headerElement.clientHeight}px`;

      setCoachingBarDistance(headerElemClientHeight);
    }

    // for sj
    if (mainSJHeader) {
      const sjHeaderClientHeight = `${mainSJHeader.offsetTop + mainSJHeader.clientHeight}px`;

      setCoachingBarDistance(sjHeaderClientHeight);
    }
  };

  useEffect(() => {
    handleCoachingBarDistance();
  }, []);

  // useEffect hooks
  useEffect(() => {
    if (!isError) {
      updateCoachingBarState();
    }

    // reset isCoachingBarUpdated flag
    setCommonContext({
      ...commonContext,
      isUpdatedFromCloset: false,
      isCoachingBarUpdated: false
    });
  }, [commonContext.isCoachingBarUpdated]);
  useEffect(() => {
    fetchCoachingBarData();

    if (fetchPlanAndClosetInfo.isFetched && fetchAppData.isFetched) {
      showWelcomeBackPopupScreen();
    }
  }, [fetchPlanAndClosetInfo.isFetched && fetchAppData.isFetched]);

  useEffect(() => {
    if (coachingBarState.displayCoachingPopup) {
      setCoachingBarState((prevState) => ({
        ...prevState,
        displayWelcomeBackPopup: false
      }));
    }

    if (coachingBarState.displayCoachingPopup && coachingPopupRef?.current) {
      // Start the slide-Up animation
      coachingPopupRef.current.style.overflow = 'hidden';
      setTimeout(() => {
        if (coachingPopupRef.current) {
          handlSlideUp(coachingPopupRef, 20);
        }
      }, 1600);
    }
  }, [coachingBarState.displayCoachingPopup]);

  return (
    <div className={`coaching-wrap ${coachingWrapClass}`}
    style={{ display: coachingBarState.displayCoachingBar ? 'block' : 'none', top: coachingBarDistance }}>
      <div className="new-subscriber-popup">
        {(coachingBarState.displayCoachingPopup) && (
        <CoachingBarPopup
          coachingBarState={coachingBarState}
          setCoachingBarState={setCoachingBarState}
          coachingPopupRef={coachingPopupRef}
          coachingBarRef={coachingBarRef}
          bodyRef={bodyRef}/>
        )}
        {(coachingBarState.displayWelcomeBackPopup) && (
        <WelcomeBackPopup
          coachingBarState={coachingBarState}
          setCoachingBarState={setCoachingBarState}
          bodyRef={bodyRef}/>
        )}
      </div>
      <div className="new-subscriber-coaching">
        {(coachingBarState.displayCoachingBar) && (
        <CoachingBar
          coachingBarState={coachingBarState}
          setCoachingBarState={setCoachingBarState}
          wiggleAnimationClass={wiggleAnimationClass}
          coachingBarRef={coachingBarRef}/>
        )}
      </div>
    </div>
  );
};

export default memo(CoachingBarContainer);
